import { normalizeRanges, range, type TimeRange } from '../time/ranges';

/**
 * Pure rules of a recording session: what can be recorded, how long it has run,
 * whether the microphone is actually hearing anything, and how much room is left.
 * No browser APIs here, so all of it is unit-testable.
 */

export type RecordingMode = 'screen' | 'screen-camera' | 'camera';

export const RECORDING_MODES: readonly RecordingMode[] = ['screen', 'screen-camera', 'camera'];

export interface CaptureSupport {
  /** `getDisplayMedia`: desktop browsers only. */
  readonly screen: boolean;
  /** `getUserMedia` (camera and microphone). */
  readonly devices: boolean;
}

/** Modes this browser can record; phones can't capture the screen, but can film the person. */
export function availableModes(support: CaptureSupport): RecordingMode[] {
  return RECORDING_MODES.filter((mode) => (mode === 'camera' ? support.devices : support.screen && (mode === 'screen' || support.devices)));
}

export const usesScreen = (mode: RecordingMode): boolean => mode !== 'camera';
export const usesCamera = (mode: RecordingMode): boolean => mode !== 'screen';

/**
 * Elapsed recording time that stands still while paused. Times are passed in
 * (milliseconds), so the clock is deterministic and testable.
 */
export class RecordingClock {
  private startedAt: number | null = null;
  private pausedAt: number | null = null;
  private pausedTotal = 0;

  start(now: number): void {
    this.startedAt = now;
    this.pausedAt = null;
    this.pausedTotal = 0;
  }

  pause(now: number): void {
    if (this.startedAt !== null && this.pausedAt === null) this.pausedAt = now;
  }

  resume(now: number): void {
    if (this.pausedAt === null) return;
    this.pausedTotal += now - this.pausedAt;
    this.pausedAt = null;
  }

  get paused(): boolean {
    return this.pausedAt !== null;
  }

  /** Seconds recorded so far. */
  elapsed(now: number): number {
    if (this.startedAt === null) return 0;
    const end = this.pausedAt ?? now;
    return Math.max(0, (end - this.startedAt - this.pausedTotal) / 1000);
  }
}

/** Meter floor: anything quieter reads as silence. */
const METER_FLOOR_DB = -60;

/** Root-mean-square of samples in -1..1 (a time-domain analyser buffer). */
export function rms(samples: ArrayLike<number>): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i]! * samples[i]!;
  return Math.sqrt(sum / samples.length);
}

/** Maps a signal level to 0..1 on a decibel scale, which matches how loud it sounds. */
export function meterLevel(level: number): number {
  if (!(level > 0)) return 0;
  const db = 20 * Math.log10(level);
  return Math.min(1, Math.max(0, (db - METER_FLOOR_DB) / -METER_FLOOR_DB));
}

/**
 * Notices a microphone that hears nothing (muted in hardware, wrong device):
 * true once the level has stayed under the threshold for `windowMs`. Any sound
 * resets it, so it never nags someone who is just pausing to think.
 */
export class SilenceWatch {
  private quietSince: number | null = null;

  constructor(
    private readonly threshold = 0.04,
    private readonly windowMs = 4000,
  ) {}

  /** Feed a meter level (0..1); returns whether the mic seems silent. */
  feed(level: number, now: number): boolean {
    if (level > this.threshold) {
      this.quietSince = null;
      return false;
    }
    this.quietSince ??= now;
    return now - this.quietSince >= this.windowMs;
  }

  reset(): void {
    this.quietSince = null;
  }
}

/**
 * Rough storage cost per minute (bytes): a sharp 1080p screen plus camera and
 * voice. Real files are usually smaller (static screens compress well), so
 * estimates err on the safe side.
 */
const BYTES_PER_MINUTE: Readonly<Record<RecordingMode, number>> = {
  screen: 75e6,
  'screen-camera': 110e6,
  camera: 45e6,
};

/** Keep this much free so the browser (and the export afterwards) still has room. */
export const STORAGE_RESERVE = 300e6;

export type StorageVerdict = 'ok' | 'low' | 'full';

export interface StorageBudget {
  readonly verdict: StorageVerdict;
  /** Estimated minutes of recording that fit, or null when the browser doesn't say. */
  readonly minutes: number | null;
}

/** How much recording fits in the free space; `low` under ~20 minutes, `full` under one. */
export function storageBudget(freeBytes: number | null, mode: RecordingMode): StorageBudget {
  if (freeBytes === null || !Number.isFinite(freeBytes)) return { verdict: 'ok', minutes: null };
  const minutes = Math.max(0, Math.floor((freeBytes - STORAGE_RESERVE) / BYTES_PER_MINUTE[mode]));
  return { verdict: minutes < 1 ? 'full' : minutes < 20 ? 'low' : 'ok', minutes };
}

/** True when the recording must stop to leave the reserve free. */
export const outOfRoom = (written: number, freeAtStart: number | null): boolean =>
  freeAtStart !== null && written >= freeAtStart - STORAGE_RESERVE;

const pad = (value: number) => String(value).padStart(2, '0');

/** `recording-2026-10-14-2155`: sortable, readable and safe as a file name. */
export function recordingName(date: Date): string {
  return `recording-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}`;
}

/** Countdown before recording starts, so there is time to switch windows. */
export const COUNTDOWN_SECONDS = 3;

/** Why a capture could not start, in terms the person can act on. */
export type CaptureProblem = 'cancelled' | 'denied' | 'system-denied' | 'not-found' | 'in-use' | 'unsupported' | 'unknown';

/**
 * Maps a `getDisplayMedia` / `getUserMedia` failure (its DOMException name) to
 * a problem. Closing the screen picker also reports `NotAllowedError`, and that
 * is a change of mind, not an error worth a warning — unless the operating
 * system blocked it (macOS asks apps for screen-recording permission).
 */
export function captureProblem(errorName: string | undefined, source: 'screen' | 'device', message = ''): CaptureProblem {
  switch (errorName) {
    case 'NotAllowedError':
    case 'SecurityError':
      if (/system/i.test(message)) return 'system-denied';
      return source === 'screen' ? 'cancelled' : 'denied';
    case 'AbortError':
      return source === 'screen' ? 'cancelled' : 'in-use';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'not-found';
    case 'NotReadableError':
      return 'in-use';
    case 'TypeError':
    case 'NotSupportedError':
      return 'unsupported';
    default:
      return 'unknown';
  }
}

/**
 * Takes within one recording. "Retake" throws away the current take: the time
 * from its start until the next take begins becomes a cut, so the editor opens
 * without it (and can still bring it back). Times are recording seconds.
 */
export class TakeLog {
  private start = 0;
  private discarded: TimeRange[] = [];
  private number = 1;

  /** The number of the take being recorded (1 for the first). */
  get take(): number {
    return this.number;
  }

  /** A fresh take starts here (after a pause, the part before it is kept). */
  begin(time: number): void {
    this.start = time;
  }

  /** Discards the current take, up to `until`, where the next take begins. */
  retake(until: number): void {
    if (until > this.start) this.discarded = normalizeRanges([...this.discarded, range(this.start, until)]);
    this.start = until;
    this.number++;
  }

  /** Everything thrown away so far. */
  get cuts(): readonly TimeRange[] {
    return this.discarded;
  }
}

/** Cuts saved next to a recording, read back defensively (the file may be from an older version or damaged). */
export function parseSavedCuts(text: string | null): TimeRange[] {
  if (!text) return [];
  try {
    const value = (JSON.parse(text) as { cuts?: unknown }).cuts;
    if (!Array.isArray(value)) return [];
    const valid = value.filter(
      (item): item is TimeRange =>
        typeof item === 'object' && item !== null && Number.isFinite((item as TimeRange).start) && Number.isFinite((item as TimeRange).end) && (item as TimeRange).end > (item as TimeRange).start,
    );
    return normalizeRanges(valid.map((item) => range(Math.max(0, item.start), item.end)));
  } catch {
    return [];
  }
}

/** How fast speaker notes scroll while recording. */
export type NotesSpeed = 'slow' | 'normal' | 'fast';
export const NOTES_SPEEDS: readonly NotesSpeed[] = ['slow', 'normal', 'fast'];

/** Scroll speed in CSS pixels per second for notes set at 20px (about 1 / 1.6 / 2.4 lines a second). */
export const NOTES_SCROLL_PX: Readonly<Record<NotesSpeed, number>> = { slow: 16, normal: 26, fast: 38 };
